/**
 * SupportInsight — the helpdesk synthesis strip atop the Support screen: the cross-lab ticket statut
 * distribution + the "à traiter" / total / unread headline figures. It is DERIVED, schema-free: it reads the
 * `parStatut` / `ouverts` aggregate the list envelope already carries (the server groups by statut over the
 * WHOLE filtered set, independent of the page slice — so the screen's one-row probe read suffices) plus the
 * cross-lab unread badge. No extra endpoint, no new table — the strip refreshes in lock-step with the inbox
 * whenever a chat send / mark-read invalidates the management root.
 *
 * Tones pair color with the statut label + the count (never color-only — daltonisme + médico-légal). Numerics
 * ride an LTR island. Token-driven throughout (no raw hex, L-2).
 */
import { useMemo } from "react";
import { Card } from "@/ui";
import { useT } from "@/app";
import { useSupportTickets, useSupportUnread } from "../api";
import { ticketStatutTone } from "../lib";
import { statutLabelKey } from "../support.lib";
import type { TicketStatut } from "../types";
import { DistributionBar, StatChips, type DistributionSlice } from "./Distribution";

/** The statut display order for the meter (open work first, settled last). */
const STATUT_ORDER: ReadonlyArray<TicketStatut> = ["ouvert", "en_cours", "resolu", "ferme"];

export function SupportInsight({ enabled }: { enabled: boolean }) {
  const { t } = useT();
  // The one-row probe carries the full cross-lab `parStatut` / `ouverts` aggregate (computed server-side over
  // the whole set, not the page). Reuses the same key the inbox probe uses, so this is a cache hit, not a
  // second network call.
  const tickets = useSupportTickets({ page: 1, page_size: 1 }, enabled);
  const unread = useSupportUnread(enabled);

  const ouverts = tickets.data?.ouverts ?? 0;
  const total = tickets.data?.total ?? 0;

  const slices = useMemo<DistributionSlice[]>(() => {
    const parStatut = tickets.data?.parStatut ?? {};
    return STATUT_ORDER.filter((s) => (parStatut[s] ?? 0) > 0).map((s) => ({
      key: s,
      label: t(statutLabelKey(s)),
      value: parStatut[s] ?? 0,
      tone: ticketStatutTone(s),
    }));
  }, [tickets.data, t]);

  // Hide until the probe resolves — the parent screen has already cleared loading/offline/forbidden, so an
  // unresolved probe here just means "no insight yet"; collapse rather than show a zeroed strip.
  if (tickets.isLoading || total <= 0) return null;

  return (
    <Card title={t("mgmt.support.insight.title")}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        <StatChips
          chips={[
            {
              key: "open",
              label: t("mgmt.support.insight.open"),
              value: ouverts,
              tone: ouverts > 0 ? "warn" : "success",
            },
            { key: "total", label: t("mgmt.support.insight.total"), value: total },
            {
              key: "unread",
              label: t("mgmt.support.insight.unread"),
              value: unread.data?.unread ?? 0,
              tone: (unread.data?.unread ?? 0) > 0 ? "danger" : "neutral",
            },
          ]}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
          <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-text-2, var(--nv-slate))" }}>
            {t("mgmt.support.insight.byStatut")}
          </span>
          <DistributionBar
            slices={slices}
            emptyLabel={t("mgmt.support.insight.empty")}
            ariaLabel={t("mgmt.support.insight.byStatut.aria")}
          />
        </div>
      </div>
    </Card>
  );
}
